import arrayRemoveItem from '../../helpers/arrayRemoveItem';
import PeerConnection from './peer_connection';
import createSubscriberPeerConnectionQueueDefault from './subscriberPeerConnectionQueue';
import createPeerConnectionSDPDefault from './peerConnectionSDP';
import SDPHelpersDefault from './sdp_helpers';
import waitUntilFactory from '../../helpers/waitUntil';

const PeerConnectionDefault = PeerConnection();
const waitUntil = waitUntilFactory();

class SinglePeerConnectionController {
  constructor(session, deps = {}) {
    this.createPeerConnectionSDP = deps.createPeerConnectionSDP || createPeerConnectionSDPDefault;
    this._session = session;
    this.PeerConnection = deps.PeerConnection || PeerConnectionDefault;
    this.sdpHelpers = deps.helpers || SDPHelpersDefault;
    this.createSubscriberPeerConnectionQueue = deps.createSubscriberPeerConnectionQueue ||
     createSubscriberPeerConnectionQueueDefault;
    // Allocate 1s to allow peer connections to close by default. When no all PCs have been closed,
    // we are safe to timeout since remaining PCs disconnection will be noop.
    this._drainTimeout = deps.drainTimeout || 1000;
    this._init();
  }

  _init() {
    this._reset();
  }

  _reset() {
    this._originalSendMessages = {};
    this._tracks = [];
    this._currentAndPreviousMids = [];
    this._subscriberPcs = {};
    this._subscriberTracks = {};
    this._subscriberMids = {};
    this._peerConnectionSDP = this.createPeerConnectionSDP({ logEvent: this._session.logEvent });
    this._subscriberQueue = this.createSubscriberPeerConnectionQueue();
    this._addingSubscriberQueueIds = {};
    if (this._peerConnection) {
      this._peerConnection.disconnect();
      this._peerConnection = null;
    }
  }

  _parseOptions(options) {
    return {
      ...options,
      // Create only one RTCDtlsTransport
      bundlePolicy: 'max-bundle',
      sendMessage: (type, payload) => {
        const content = {
          ...payload,
        };

        let midsToAdd;
        if (content.sdp) {
          const { sdp, mids } = this._peerConnectionSDP.getPartialSdp(type, content.sdp);
          content.sdp = sdp;
          midsToAdd = mids;
        }

        const { send, completion, subscriberPcId } = this._getSendMethod(midsToAdd, type, content);

        send?.(type, content, completion);

        if (type === 'answer') {
          // We only send an answer when Mantis previously sent an offer, i.e. we added a new
          // subscriber. Therefore at this point we can consider the negotiation done.
          this.subscriberComplete(subscriberPcId);
        }
      },
    };
  }

  _addListeners() {
    if (!this._peerConnection) {
      return;
    }

    // override the trackAdded listener so we can route the tracks to the proper subscriber.
    this._peerConnection.on('trackAdded', (ev) => {
      const { track, transceiver } = ev;
      const subscriberPcId = Object.keys(this._subscriberMids).find(subPcId =>
        this._subscriberMids[subPcId].includes(transceiver.mid));
      if (!subscriberPcId) {
        return;
      }
      const subscriberPc = this._subscriberPcs[subscriberPcId];
      if (!subscriberPc || this._currentAndPreviousMids.includes(transceiver.mid)) {
        return;
      }
      // We store all tracks per Subscriber.
      this._subscriberTracks[subscriberPcId].push(track.id);
      // Also store all tracks per SinglePeerConnection
      this._tracks.push(track.id);
      this._currentAndPreviousMids.push(transceiver.mid);
      this._peerConnectionSDP.addSubscriberMid(transceiver.mid, subscriberPcId);
      subscriberPc.onTrackAdded({ track, transceiver });
    });
  }

  async _generateOffer() {
    return new Promise((resolve) => {
      const createAnswer = (offer) => {
        this._peerConnectionSDP.removeExtensions(offer.sdp);
        const answer = this._peerConnectionSDP.createSdp();
        this._peerConnection.processMessage('answer', { content: { sdp: answer } });
        resolve();
      };
      this._peerConnection.generateOffer(createAnswer);
    });
  }

  _removeMessageSender(subscriberPcId) {
    const isLastUsed = this._originalSendMessages[subscriberPcId] === this._latestSendMessage;
    delete this._originalSendMessages[subscriberPcId];
    if (isLastUsed) {
      this._latestSendMessage = null;
    }
  }

  _removeSubscriber(subscriberPcId) {
    delete this._subscriberPcs[subscriberPcId];
    this._tracks = this._tracks.filter(trackId =>
      !(this._subscriberTracks[subscriberPcId] || []).includes(trackId));
    delete this._subscriberTracks[subscriberPcId];
    delete this._subscriberMids[subscriberPcId];
    this._removeMessageSender(subscriberPcId);
  }

  _stopTransceivers(transceiverIds) {
    // Update the saved remote description.
    this._peerConnectionSDP.removeMids(transceiverIds);
    // Stop transceivers in local description.
    this._peerConnection.stopTransceivers(transceiverIds);
  }

  _setTransceiverToInactive(transceiverId) {
    // Update the saved remote description.
    this._peerConnectionSDP.setTrackToInactive(transceiverId);
    // Set transceivers to inactive in local description.
    this._peerConnection.setTransceiversToInactive([transceiverId]);
  }

  _getLatestSendMessage() {
    return Object.values(this._originalSendMessages).slice(-1)[0];
  }

  _getSendMethod(midsToAdd, type, content) {
    if (midsToAdd?.length) {
      // If there are mids to add, we know the corresponding subscriber,
      // so we can find the correct sendMessage.
      const subscriberPcId = Object.keys(this._subscriberMids).find(subPcId =>
        this._subscriberMids[subPcId].some(mid => midsToAdd.includes(mid)));
      if (subscriberPcId) {
        this._latestSendMessage = this._originalSendMessages[subscriberPcId];
        return {
          send: this._latestSendMessage,
          subscriberPcId,
          completion: this._createSendMessageErrorHandler(type, content, subscriberPcId),
        };
      }
    }

    if (!this._latestSendMessage) {
      this._latestSendMessage = this._getLatestSendMessage();
    }
    return {
      send: this._latestSendMessage,
      completion: this._createSendMessageErrorHandler(type, content),
    };
  }

  _createSendMessageErrorHandler(type, content, subscriberPcId) {
    const handler = (error, message) => {
      // If the corresponding stream does not longer exist, we are going to try using
      // another sendMessage.
      if (error && message?.status === '404') {
        if (subscriberPcId) {
          // We delete this sendMessage because its correponding stream doesn't exist.
          // But we don't try to resend the message, because we know this message
          // corresponds to a specific subscriber.
          this._removeMessageSender(subscriberPcId);
          return;
        }
        const subscriberPcIds = Object.keys(this._originalSendMessages);
        if (subscriberPcIds.length) {
          const id = subscriberPcIds.find(subPcId =>
            this._originalSendMessages[subPcId] === this._latestSendMessage);
          // We delete this sendMessage because its correponding stream doesn't exist.
          this._removeMessageSender(id);
          this._latestSendMessage = this._getLatestSendMessage();
          // We try to send the message again with another subscriber's sendMessage.
          this._latestSendMessage?.(type, content, handler);
        }
      }
    };
    return handler;
  }

  // Singleton to make sure we are using only one PC when SPC. If SPC, we will add Subscriber
  // specific options to handle analytics and negotiation per Subscriber. This will take care
  // to send the answer back to Rumor by its respective Subscriber and not multiple answers.
  // It will instantiate a new regular PC for all other cases.
  getPeerConnection(opt, subscriberPc) {
    this._subscriberPcs[opt.widgetId] = subscriberPc;
    this._originalSendMessages[opt.widgetId] = opt.sendMessage;

    if (this._peerConnection) {
      this._peerConnection.addOptions(opt);
    } else {
      const parsedOptions = this._parseOptions(opt);
      this._peerConnection = new this.PeerConnection(parsedOptions);

      // Add listeners, which will take care all the tracks for all the subs and if any
      // negotiation needed.
      this._addListeners();
    }

    this._subscriberTracks[opt.widgetId] = [];
    this._subscriberMids[opt.widgetId] = [];

    return this._peerConnection;
  }

  removeSubscriber(subscriberPcId) {
    if (this._addingSubscriberQueueIds[subscriberPcId]) {
      // If the subscriber is being added, we cancel the task.
      const queueTaskId = this._addingSubscriberQueueIds[subscriberPcId];
      delete this._addingSubscriberQueueIds[subscriberPcId];
      this._subscriberQueue.cancel(queueTaskId);
    }
    const queueTaskId = this._subscriberQueue.enqueue(async () => {
      let transceiversIdsToStop = this._subscriberMids[subscriberPcId] || [];
      const isHeadMid = this._peerConnectionSDP.isHead(subscriberPcId);

      // Remove the subscriber at controller level.
      this._removeSubscriber(subscriberPcId);

      if (!Object.keys(this._subscriberTracks).length) {
        // Reset all components since we have no subscriber left. Then return,
        // nothing else to do here.
        this._reset();
        return;
      }

      // Remove the subscriber from the PC. If the corresponding media section isn't the first mSection,
      // this section won't own the credentials for the PC, thus we are safe to stop transceiver and remove it.
      // However if the media section is first in the SDP, it will own the credentials, so we set it to inactive instead of removing it.
      // This will prevent an iceRestart.
      if (isHeadMid) {
        const headMid = this._peerConnectionSDP.getHeadMid();
        this._setTransceiverToInactive(headMid);
        transceiversIdsToStop = transceiversIdsToStop.filter(id => id !== headMid);
      }

      this._stopTransceivers(transceiversIdsToStop);
      await this._generateOffer();

      this._subscriberQueue.dequeueAndProcessNext(queueTaskId);
    });
  }

  generateOfferWithIceRestart() {
    this._peerConnection.iceRestart();
    this._peerConnection.generateOfferAndSend();
  }

  removeTrack(track, subscriberPcId) {
    const trackId = track?.id;
    if (trackId) {
      arrayRemoveItem(this._tracks, trackId);
      arrayRemoveItem(this._subscriberTracks[subscriberPcId] || [], trackId);
    }
  }

  destroy() {
    this._reset();
    this._session = null;
  }

  addSubscriber(subscriberPcId, subscriberCreate) {
    const taskId = this._subscriberQueue.enqueue(subscriberCreate);
    this._addingSubscriberQueueIds[subscriberPcId] = taskId;
  }

  subscriberComplete(subscriberPcId) {
    const taskId = this._addingSubscriberQueueIds[subscriberPcId];
    delete this._addingSubscriberQueueIds[subscriberPcId];
    this._subscriberQueue.dequeueAndProcessNext(taskId);
  }

  processAnswer(sdp) {
    return this._peerConnectionSDP.processAnswer(sdp);
  }

  async processOffer(sdp, subscriberPcId) {
    const processedOffer = this._peerConnectionSDP.processOffer(sdp);
    this._subscriberMids[subscriberPcId] = processedOffer.mids;
    return processedOffer.sdp;
  }

  async closeAfterSubscriberRemovals() {
    // Allow controller to clear all subscribers before closing.
    try {
      await waitUntil(() =>
        Object.keys(this._subscriberTracks).length === 0
      , { timeout: this._drainTimeout });
    } catch (e) {
      // This means no all subscribers haven't been cleared. Proceed to close since it already
      // times out.
    }
    this.destroy();
  }
}

export default SinglePeerConnectionController;
